import { doc, setDoc, addDoc, deleteDoc, collection, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '@/config/firebase';
import { getAuth } from 'firebase/auth';

/**
 * Service to WRITE salary data to the subcollection structure (post-migration)
 *
 * Structure:
 * collaborators_private/{id}/salary/current
 * collaborators_private/{id}/salary/current/history/{historyId}
 */

export interface SalaryUpdateData {
  // Current salary fields
  newSalaryEntryAmount?: number;
  newSalaryEntryExtras?: number;
  newSalaryEntrySalaryType?: 'CLT' | 'PJ' | 'Estagiário' | 'Sócio';
  newSalaryEntryDate?: string;
  newSalaryEntryReason?: string;
  newSalaryEntryObservations?: string;

  // History config
  salaryHistoryAllowRemove?: boolean;
  salaryHistoryShowHistory?: boolean;

  // History array (if updating multiple entries)
  salaryHistorySalaries?: Array<{
    id?: string;  // Existing entries have ID
    amount: number;
    extras: number;
    salaryType: string;
    date: string;
    reason: string;
    observations?: string;
  }>;

  // IDs to delete from history
  salaryHistoryDeletedIds?: string[];
}

/**
 * Extract salary fields from form data
 */
export function extractSalaryFields(formData: any): {
  salaryFields: SalaryUpdateData | null;
  nonSalaryFields: any;
} {
  const salaryFieldNames = [
    'newSalaryEntryAmount',
    'newSalaryEntryExtras',
    'newSalaryEntrySalaryType',
    'newSalaryEntryDate',
    'newSalaryEntryReason',
    'newSalaryEntryObservations',
    'salaryHistoryAllowRemove',
    'salaryHistoryShowHistory',
    'salaryHistorySalaries',
    'salaryHistoryDeletedIds',
  ];

  const salaryFields: any = {};
  const nonSalaryFields: any = {};
  let hasSalaryData = false;

  for (const key in formData) {
    if (salaryFieldNames.includes(key)) {
      if (formData[key] !== undefined && formData[key] !== null) {
        salaryFields[key] = formData[key];
        hasSalaryData = true;
      }
    } else {
      nonSalaryFields[key] = formData[key];
    }
  }

  return {
    salaryFields: hasSalaryData ? salaryFields : null,
    nonSalaryFields
  };
}

/**
 * Update salary data in subcollection
 */
export async function updateSalaryInSubcollection(
  collaboratorId: string,
  salaryData: SalaryUpdateData
): Promise<void> {
  const auth = getAuth();
  const currentUser = auth.currentUser;

  if (!currentUser) {
    throw new Error('User must be authenticated to update salary');
  }

  const userId = currentUser.uid;

  // DEBUG: Log completo dos dados recebidos
  console.log('🔍 updateSalaryInSubcollection - dados recebidos:', {
    collaboratorId,
    hasDeletedIds: !!(salaryData.salaryHistoryDeletedIds?.length),
    deletedIds: salaryData.salaryHistoryDeletedIds,
    deletedIdsCount: salaryData.salaryHistoryDeletedIds?.length,
    allSalaryDataKeys: Object.keys(salaryData)
  });

  try {
    // Reference to salary/current document
    const salaryRef = doc(
      db,
      'collaborators_private',
      collaboratorId,
      'salary',
      'current'
    );

    const salaryDoc = await getDoc(salaryRef);
    const timestamp = serverTimestamp();
    const configUpdates: Record<string, any> = {
      updatedAt: timestamp,
      updatedBy: userId,
    };

    if (salaryData.salaryHistoryAllowRemove === true) {
      configUpdates.allowRemove = true;
    } else if (salaryData.salaryHistoryAllowRemove === false) {
      configUpdates.allowRemove = false;
    }

    if (salaryData.salaryHistoryShowHistory === true) {
      configUpdates.showHistory = true;
    } else if (salaryData.salaryHistoryShowHistory === false) {
      configUpdates.showHistory = false;
    }

    if (!salaryDoc.exists()) {
      configUpdates.allowRemove = configUpdates.allowRemove ?? true;
      configUpdates.showHistory = configUpdates.showHistory ?? true;
      configUpdates.createdAt = timestamp;
      configUpdates.createdBy = userId;
    }

    await setDoc(salaryRef, configUpdates, { merge: true });

    // Handle salary history DELETIONS first
    if (salaryData.salaryHistoryDeletedIds && Array.isArray(salaryData.salaryHistoryDeletedIds)) {
      for (const deletedId of salaryData.salaryHistoryDeletedIds) {
        try {
          const historyDocRef = doc(salaryRef, 'history', deletedId);
          await deleteDoc(historyDocRef);
        } catch (error) {
          console.error('Error deleting history entry:', deletedId, error);
        }
      }
    }

    // Handle salary history updates - ONLY add NEW entries
    const pendingHistoryEntries: Array<{
      id?: string;
      amount: number;
      extras?: number;
      salaryType: string;
      date: string;
      reason?: string;
      observations?: string;
    }> = [];

    if (Array.isArray(salaryData.salaryHistorySalaries)) {
      pendingHistoryEntries.push(...salaryData.salaryHistorySalaries);
    }

    if (
      salaryData.newSalaryEntryAmount !== undefined &&
      salaryData.newSalaryEntryDate
    ) {
      pendingHistoryEntries.push({
        amount: salaryData.newSalaryEntryAmount,
        extras: salaryData.newSalaryEntryExtras ?? 0,
        salaryType: salaryData.newSalaryEntrySalaryType ?? 'CLT',
        date: salaryData.newSalaryEntryDate,
        reason: salaryData.newSalaryEntryReason ?? '',
        observations: salaryData.newSalaryEntryObservations ?? '',
      });
    }

    if (pendingHistoryEntries.length > 0) {
      const historyRef = collection(salaryRef, 'history');

      for (const historyEntry of pendingHistoryEntries) {
        if (historyEntry.id) {
          console.log('⏭️  Skipping existing history entry with ID:', historyEntry.id);
          continue;
        }

        const historyData = {
          amount: historyEntry.amount,
          extras: historyEntry.extras ?? 0,
          salaryType: historyEntry.salaryType,
          changeDate: historyEntry.date,
          changeReason: historyEntry.reason || '',
          changeObservations: historyEntry.observations || '',
          recordedAt: serverTimestamp(),
          recordedBy: userId,
        };

        await addDoc(historyRef, historyData);
        console.log('✅ New salary history entry added:', historyData);
      }
    }

    console.log(`✅ Salary data saved to subcollection for collaborator ${collaboratorId}`);
  } catch (error) {
    console.error('Error updating salary in subcollection:', error);
    throw error;
  }
}

/**
 * Check if form data contains salary fields
 */
export function hasSalaryData(formData: any): boolean {
  const salaryFieldNames = [
    'newSalaryEntryAmount',
    'newSalaryEntryExtras',
    'newSalaryEntrySalaryType',
    'newSalaryEntryDate',
    'newSalaryEntryReason',
    'newSalaryEntryObservations',
    'salaryHistoryAllowRemove',
    'salaryHistoryShowHistory',
    'salaryHistorySalaries',
    'salaryHistoryDeletedIds',
  ];

  return salaryFieldNames.some(field =>
    formData[field] !== undefined && formData[field] !== null
  );
}
